// Paylaşılan Modal bileşeni — pencere stili: icon + title + subtitle, sağda X
// Footer 2 zonlu: sol (yıkıcı), sağ (iptal + birincil)
//
// YENI API (önerilen):
//   <Modal
//     icon={<UserIcon/>}                 // sol üstte ikon
//     title="Cariyi Düzenle"
//     subtitle={<>{form.code} · <Badge>Borç ₺X</Badge></>}
//     size="lg"
//     onClose={...}
//     primaryAction={{ label: 'Kaydet', onClick: save, loading: saving, icon: <SaveIcon/> }}
//     cancelAction={{ label: 'İptal', onClick: cancel }}
//     destructiveAction={{ label: 'Sil', onClick: del, icon: <TrashIcon/> }}
//   >
//     ...content...
//   </Modal>
//
// ESKI API (geriye uyum) — hala destekleniyor:
//   <Modal title="..." onClose={...} onSave={...} saving={...}>...</Modal>
//   <Modal title="..." headerActions={<Button>X</Button>} footer={<>...</>}>...</Modal>
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

const SIZE_MAX = { sm: 420, md: 560, lg: 820, xl: 1100, full: '96vw' };

// İç içe modal Escape yönetimi — yalnızca EN ÜSTTEKİ modal Escape'e cevap verir.
// (Aksi halde nested modalda Escape hem alt hem üst modalın onClose'unu tetikler.)
const __modalEscStack = [];

export default function Modal({
  icon = null,
  title,
  subtitle = null,
  size = 'md',
  onClose,

  // Yeni API
  primaryAction = null,        // { label, onClick, loading, icon, variant='primary', disabled }
  secondaryAction = null,      // { label, onClick, loading, icon, variant='ghost', disabled } — primary'nin solunda (örn "Taslak Kaydet")
  cancelAction = null,         // { label, onClick, disabled }
  destructiveAction = null,    // { label, onClick, icon, disabled } — sol-altta

  // Eski API (geriye uyum)
  onSave = null,
  saveLabel = null,
  cancelLabel = null,
  saving = false,
  headerActions = null,
  footer = null,

  closeOnOverlay = false,  // Default: dış tıklamayla kapanmaz — kullanıcı X / Esc / iptal ile kapatır
  bodyStyle = null,
  bodyClassName = '',
  overlayStyle = null, // İç içe modal için z-index override
  children,
}) {
  const { t } = useTranslation();
  // Default fallback'ler i18n'den (saveLabel/cancelLabel verilirse o öncelikli)
  saveLabel = saveLabel || t('common.save');
  cancelLabel = cancelLabel || t('common.cancel');
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const escIdRef = useRef(null);
  if (escIdRef.current === null) escIdRef.current = {};
  useEffect(() => {
    const id = escIdRef.current;
    __modalEscStack.push(id);
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Yalnızca en üstteki (son açılan) modal cevap versin
      if (__modalEscStack[__modalEscStack.length - 1] !== id) return;
      const cb = onCloseRef.current;
      if (cb) { e.stopPropagation(); cb(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = __modalEscStack.indexOf(id);
      if (i >= 0) __modalEscStack.splice(i, 1);
    };
  }, []);

  const max = SIZE_MAX[size] || SIZE_MAX.md;
  const maxW = typeof max === 'number' ? max + 'px' : max;

  // Yeni API → footer otomatik 2 zonlu
  const hasNewActions = primaryAction || secondaryAction || cancelAction || destructiveAction;
  const newFooter = hasNewActions ? (
    <>
      <div className="ui-modal-footer-left">
        {destructiveAction && (
          <Button variant="danger" icon={destructiveAction.icon}
            onClick={destructiveAction.onClick} disabled={destructiveAction.disabled}>
            {destructiveAction.label}
          </Button>
        )}
      </div>
      <div className="ui-modal-footer-right">
        {cancelAction && (
          <Button variant="ghost" onClick={cancelAction.onClick} disabled={cancelAction.disabled}>
            {cancelAction.label || t('common.cancel')}
          </Button>
        )}
        {secondaryAction && (
          <Button variant={secondaryAction.variant || 'ghost'} icon={secondaryAction.icon}
            loading={secondaryAction.loading} disabled={secondaryAction.disabled}
            onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button variant={primaryAction.variant || 'primary'} icon={primaryAction.icon}
            loading={primaryAction.loading} disabled={primaryAction.disabled}
            onClick={primaryAction.onClick}>
            {primaryAction.label || t('common.save')}
          </Button>
        )}
      </div>
    </>
  ) : null;

  // Eski API fallback
  const legacyFooter = onSave ? (
    <>
      <div className="ui-modal-footer-left" />
      <div className="ui-modal-footer-right">
        {onClose && <Button variant="ghost" onClick={onClose}>{cancelLabel}</Button>}
        <Button onClick={onSave} loading={saving}>{saveLabel}</Button>
      </div>
    </>
  ) : null;

  const finalFooter = footer || newFooter || legacyFooter;
  const showHeader = title || icon || subtitle || onClose;

  return (
    <div className="ui-modal-overlay" style={overlayStyle || undefined} onClick={() => closeOnOverlay && onClose && onClose()}>
      <div className="ui-modal" style={{ maxWidth: maxW }} onClick={(e) => e.stopPropagation()}>
        {showHeader && (
          <div className="ui-modal-header">
            <div className="ui-modal-header-main">
              {icon && <div className="ui-modal-icon">{icon}</div>}
              <div className="ui-modal-titles">
                {title && <h3 className="ui-modal-title">{title}</h3>}
                {subtitle && <div className="ui-modal-subtitle">{subtitle}</div>}
              </div>
            </div>
            <div className="ui-modal-header-actions">
              {headerActions}
              {onClose && (
                <button type="button" className="ui-btn ui-btn-ghost ui-btn-sm ui-btn-icon-only" onClick={onClose} title="Kapat">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          </div>
        )}
        <div className={'ui-modal-body' + (bodyClassName ? ' ' + bodyClassName : '')} style={bodyStyle || undefined}>{children}</div>
        {finalFooter && (
          <div className="ui-modal-footer">{finalFooter}</div>
        )}
      </div>
    </div>
  );
}
